<template>
  <div class="company-report mt-8">
    <!-- Header: título + contrato + toggle en la misma fila -->
    <div class="mb-5">
      <h2 class="report-title">Reporte por empresa</h2>
      <p class="report-desc mt-1 mb-3">Selecciona un contrato para ver el reporte detallado de uso y reembolsos.</p>

      <div class="d-flex align-center flex-wrap" style="gap: 12px;">
        <v-autocomplete
          v-model="selectedContrato"
          :items="contratos"
          :loading="loadingContratos"
          :item-text="contratoLabel"
          item-value="id"
          label="Contrato"
          dense
          outlined
          hide-details
          clearable
          placeholder="Buscar por código o empresa..."
          prepend-inner-icon="mdi-file-document-outline"
          return-object
          background-color="white"
          style="max-width: 320px; min-width: 220px;"
        >
          <template v-slot:no-data>
            <v-list-item>
              <v-list-item-title>{{ loadingContratos ? 'Cargando...' : 'Sin resultados' }}</v-list-item-title>
            </v-list-item>
          </template>
          <template v-slot:item="{ item, on, attrs }">
            <v-list-item v-bind="attrs" v-on="on">
              <v-list-item-content>
                <v-list-item-title>
                  {{ item.producto && item.producto.nombre ? `${item.codigo} — ${item.producto.nombre}` : item.codigo }}
                </v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-chip x-small :color="statusColor(item.status)" dark>{{ item.status || '—' }}</v-chip>
              </v-list-item-action>
            </v-list-item>
          </template>
        </v-autocomplete>

        <div class="d-flex align-center" style="gap: 10px;">
          <span class="toggle-label">Membresías:</span>
          <v-btn-toggle v-model="basis" mandatory dense class="basis-toggle">
            <v-btn small value="active" class="toggle-btn">Activas</v-btn>
            <v-btn small value="historical" class="toggle-btn">Totales</v-btn>
          </v-btn-toggle>
          <!-- El toggle solo cambia los KPIs de "Base"; los porcentajes, el uso y los reembolsos
               no dependen de él. Sin este aviso parecería que no funciona. -->
          <span class="basis-hint" data-test="basis-hint">
            Cambia solo la Base:
            {{ basis === 'active'
              ? 'accesos vigentes al contrato y membresías activas.'
              : 'todos los accesos cargados (incluye revocados) y todas las membresías.' }}
          </span>
        </div>
      </div>
    </div>

    <!-- Vigencia del contrato. Sale de `selectedContrato`, así que aparece
         apenas se elige el contrato, sin esperar al reporte. Un contrato sin
         fecha de término lo dice explícitamente en vez de mostrar "hoy". -->
    <div v-if="contratoPeriod" class="period-note mb-4">
      <v-icon x-small color="#104B94" class="mr-1">mdi-calendar-range</v-icon>
      <span>Vigencia del contrato: <strong>{{ contratoPeriod.label }}</strong></span>
      <span v-if="contratoPeriod.note" class="period-note-hint">— {{ contratoPeriod.note }}</span>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-10">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <!-- Error de carga -->
    <div v-else-if="loadError" class="text-center red--text py-8">
      <v-icon size="40" color="red lighten-1">mdi-alert-circle-outline</v-icon>
      <div class="mt-2">Ocurrió un error al cargar el reporte. Intenta nuevamente.</div>
      <v-btn small color="primary" class="mt-3" @click="loadReport(selectedContrato.id)">
        Reintentar
      </v-btn>
    </div>

    <!-- Sin contrato seleccionado -->
    <div v-else-if="!selectedContrato" class="text-center grey--text py-8">
      <v-icon size="40" color="grey lighten-2">mdi-file-document-outline</v-icon>
      <div class="mt-2">Selecciona un contrato para ver el reporte</div>
    </div>

    <!-- Contenido del reporte -->
    <template v-else-if="report">

      <!-- KPIs en tres grupos: el contrato (plata), activación y uso (lo que se mira primero, con el
           semáforo de Customer Success) y la base sobre la que se calculan los porcentajes. -->
      <v-row class="mb-6">
        <v-col cols="12" md="6" lg="4">
          <v-card outlined class="kpi-group pa-4" data-test="kpi-group-contract">
            <div class="kpi-group-title">Contrato</div>
            <div class="kpi-pair">
              <div class="kpi-item">
                <div class="kpi-label">MRR</div>
                <v-progress-circular v-if="loadingExtraMetrics" indeterminate size="20" width="2" color="primary" class="mt-2" />
                <template v-else>
                  <div class="kpi-value kpi-value--money" :title="mrr != null ? formatMrr(mrr) : ''">{{ mrr != null ? formatMrr(mrr) : '—' }}</div>
                  <div class="kpi-foot">{{ mrrSharePct != null ? mrrSharePct + '% del MRR de Pawer' : '—' }}</div>
                </template>
              </div>
              <div class="kpi-item">
                <div class="kpi-label">% Gross Margin</div>
                <v-progress-circular v-if="loadingExtraMetrics" indeterminate size="20" width="2" color="primary" class="mt-2" />
                <div v-else class="kpi-value">{{ marginPct != null ? marginPct + '%' : '—' }}</div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" md="6" lg="4">
          <v-card outlined class="kpi-group kpi-group--focus pa-4" data-test="kpi-group-activation-usage">
            <div class="kpi-group-title">Activación y uso</div>
            <div class="kpi-pair">
              <div v-for="rate in activationUsageRates" :key="rate.key" class="kpi-item" :title="rate.tooltip">
                <div class="kpi-label">{{ rate.label }}</div>
                <div class="kpi-value" :class="scoreClass(rate.pct)" :data-test="`kpi-${rate.key}`">
                  {{ rate.pct != null ? rate.pct + '%' : '—' }}
                </div>
                <v-progress-linear
                  :value="rate.pct != null ? Math.min(rate.pct, 100) : 0"
                  :color="rate.pct != null ? scoreColor(rate.pct) : 'grey lighten-2'"
                  background-color="grey lighten-3"
                  height="6"
                  rounded
                  class="my-2"
                />
                <div class="kpi-foot">{{ rate.foot }}</div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" lg="4">
          <v-card outlined class="kpi-group pa-4" data-test="kpi-group-base">
            <div class="kpi-group-title">Base</div>
            <div class="kpi-trio">
              <div v-for="item in baseItems" :key="item.key" class="kpi-item">
                <div class="kpi-label">{{ item.label }}</div>
                <div class="kpi-value kpi-value--small" :data-test="`kpi-base-${item.key}`">{{ item.value }}</div>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Uso por sección -->
      <div class="section-title mb-3">Detalle de Uso</div>

      <!-- El rango vive bajo este título y no encabezando la vista: gobierna el
           uso y los reembolsos, no los KPIs de arriba, que son la foto de hoy. -->
      <div class="d-flex align-center flex-wrap mb-3" style="gap: 8px;">
          <v-menu
            v-model="fromDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
            :z-index="9999"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="fromDate"
                label="Desde"
                prepend-icon="mdi-calendar"
                readonly
                dense
                outlined
                hide-details
                style="max-width: 165px;"
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="fromDate"
              :max="toDate || todayDate"
              @input="fromDateMenu = false"
            ></v-date-picker>
          </v-menu>

          <v-menu
            v-model="toDateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            min-width="auto"
            :z-index="9999"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="toDate"
                label="Hasta"
                prepend-icon="mdi-calendar"
                readonly
                dense
                outlined
                hide-details
                style="max-width: 165px;"
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="toDate"
              :min="fromDate"
              :max="todayDate"
              @input="toDateMenu = false"
            ></v-date-picker>
          </v-menu>

          <v-btn
            small
            height="40"
            color="primary"
            :disabled="!hasPendingPeriodChange || loading"
            @click="applyPeriod()"
          >
            <v-icon small left>mdi-magnify</v-icon>
            Buscar
          </v-btn>

          <v-btn
            small
            height="40"
            outlined
            color="primary"
            :disabled="!periodIsCustom || loading"
            @click="resetPeriodToContrato()"
          >
            Vigencia completa
          </v-btn>

          <!-- Sin esto, cambiar las fechas y no buscar dejaría los selectores
               mostrando un rango y las cifras otro, sin señal de por medio. -->
          <span v-if="hasPendingPeriodChange" class="period-pending-hint">
            Presiona Buscar para aplicar el período
          </span>
      </div>

      <!-- Se declara siempre el período aplicado. Cuando es el contrato
           completo se dice así; cuando está acotado, se marca para que las
           cifras más bajas no se lean como las del contrato entero. -->
      <div
        v-if="appliedPeriodLabel"
        class="applied-period mb-3"
        :class="{ 'applied-period--custom': !appliedPeriodIsFullContract }"
      >
        <v-icon x-small :color="appliedPeriodIsFullContract ? '#104B94' : '#B26500'" class="mr-1">
          mdi-filter-variant
        </v-icon>
        <span v-if="appliedPeriodIsFullContract">
          Mostrando el período completo del contrato: <strong>{{ appliedPeriodLabel }}</strong>
        </span>
        <span v-else>
          Período acotado: <strong>{{ appliedPeriodLabel }}</strong> — las cifras de abajo
          cubren solo este rango, no el contrato completo
        </span>
      </div>

      <!-- No se puede descartar: mientras el rango esté fuera de la vigencia,
           el aviso se queda. Su ausencia es lo que convierte un dato correcto
           en una cifra engañosa. -->
      <v-alert
        v-if="periodWarning"
        dense
        text
        type="warning"
        class="period-alert mb-3"
      >
        {{ periodWarning }}
      </v-alert>

      <!-- Mismo patrón que los KPIs: los totales como foco y cada sección de la app en un
           solo grupo. Los números siguen abriendo el detalle de usuarios. -->
      <v-row class="mb-6">
        <v-col cols="12" lg="4">
          <v-card outlined class="kpi-group kpi-group--focus pa-4" data-test="usage-group-totals">
            <div class="kpi-group-title">Totales</div>
            <div class="kpi-pair">
              <div class="kpi-item">
                <div class="usage-item-head">
                  <v-icon small color="#104B94" class="mr-2">mdi-sigma</v-icon>
                  <span class="section-card-title">Total de usos</span>
                </div>
                <div class="usage-count usage-clickable" style="color:#104B94;" @click="openTotalUsosDetail">{{ totalUsos }}</div>
                <div class="usage-sub">reembolsos aprobados/pagados + secciones</div>
              </div>
              <div class="kpi-item">
                <div class="usage-item-head">
                  <v-icon small color="#009688" class="mr-2">mdi-cash-refund</v-icon>
                  <span class="section-card-title">Total de reembolsos</span>
                </div>
                <div
                  class="usage-count"
                  :class="{ 'usage-clickable': canOpenUsage('refunds') }"
                  style="color:#009688;"
                  @click="openUsageDetail({ key: 'refunds', label: 'Reembolsos' })"
                >{{ totalReembolsos }}</div>
                <div class="usage-sub">app + convenio clínica + operativos</div>
              </div>
            </div>
          </v-card>
        </v-col>

        <v-col cols="12" lg="8">
          <v-card outlined class="kpi-group pa-4" data-test="usage-group-sections">
            <div class="kpi-group-title">Por sección</div>
            <div class="usage-sections">
              <div v-for="section in usageSections" :key="section.key" class="kpi-item">
                <div class="usage-item-head">
                  <v-icon small :color="section.color" class="mr-2">{{ section.icon }}</v-icon>
                  <span class="section-card-title">{{ section.label }}</span>
                </div>
                <div
                  class="usage-count"
                  :class="{ 'usage-clickable': !section.mock && section.total > 0 && canOpenUsage(section.key) }"
                  @click="!section.mock && section.total > 0 && openUsageDetail(section)"
                >{{ section.mock ? '—' : section.total }}</div>
                <div class="usage-sub">
                  <template v-if="section.mock">sin tracking aún</template>
                  <template v-else>{{ section.uniqueUsers }} usuarios</template>
                </div>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Reembolsos -->
      <div class="section-title mb-4">Detalle de Reembolsos</div>
      <v-card outlined class="pa-5 mb-5">
        <v-row align="center">
          <!-- Donut -->
          <v-col cols="12" md="5" class="d-flex justify-center align-center pr-md-6">
            <refund-donut-chart
              :metrics="donutMetrics"
              :total="refundTotal"
              :clickable="canOpenUsage('refunds')"
              @click-total="openUsageDetail({ key: 'refunds', label: 'Reembolsos' })"
            />
          </v-col>

          <!-- Stat cards: usuarios/membresías que solicitaron + Total reembolsado + % de rechazo -->
          <v-col cols="12" md="7">
            <v-row dense>
              <v-col cols="4" sm="4">
                <v-card outlined class="stat-card pa-3 text-center" style="border-color:#009688;">
                  <div class="stat-label">Reembolsos vía App</div>
                  <div class="stat-value" style="color:#009688; font-size:20px;">{{ formatAmount(report.refunds.totalPaidAmount) }}</div>
                  <div class="stat-sub">aprobados + pagados</div>
                </v-card>
              </v-col>
              <v-col cols="4" sm="4">
                <v-card outlined class="stat-card pa-3 text-center" style="border-color:#7B1FA2;">
                  <div class="stat-label">PetMed</div>
                  <div class="stat-value" style="color:#7B1FA2; font-size:20px;">{{ formatAmount(report.refunds.petmed.total) }}</div>
                  <div class="stat-sub">{{ report.refunds.petmed.count }} atenciones</div>
                </v-card>
              </v-col>
              <v-col cols="4" sm="4">
                <v-card outlined class="stat-card pa-3 text-center" style="border-color:#3F51B5;">
                  <div class="stat-label">Operativos</div>
                  <div class="stat-value" style="color:#3F51B5; font-size:20px;">{{ formatAmount(report.refunds.operational.total) }}</div>
                  <div class="stat-sub">{{ report.refunds.operational.count }} atenciones</div>
                </v-card>
              </v-col>
              <v-col cols="4" sm="4">
                <v-card outlined class="stat-card pa-3 text-center">
                  <div class="stat-label">Usuarios solicitantes</div>
                  <div class="stat-value" style="font-size:20px;">{{ report.refunds.uniqueUsers }}</div>
                  <div class="stat-sub">han pedido reembolso</div>
                </v-card>
              </v-col>
              <v-col cols="4" sm="4">
                <v-card outlined class="stat-card pa-3 text-center">
                  <div class="stat-label">Membresías solicitantes</div>
                  <div class="stat-value" style="font-size:20px;">{{ report.refunds.uniqueMemberships }}</div>
                  <div class="stat-sub">han pedido reembolso</div>
                </v-card>
              </v-col>
              <v-col cols="4" sm="4">
                <v-card
                  outlined
                  class="stat-card pa-3 text-center"
                  :style="{ borderColor: '#F44336', cursor: canSeeRefundUsers ? 'pointer' : 'default' }"
                  @click="openRejectionReasons"
                >
                  <div class="stat-label">% de rechazo</div>
                  <div class="stat-value" style="color:#F44336;">{{ rejectionRate }}%</div>
                  <div class="stat-sub">{{ report.refunds.rejected }} rechazados<template v-if="canSeeRefundUsers"> · ver motivos</template></div>
                </v-card>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <!-- Tabla desglose por tipo -->
        <template v-if="report.refunds.byService.length">
          <v-divider class="my-4" />
          <div class="section-card-title mb-3">Breakdown de Reembolsos</div>
          <v-simple-table dense>
            <thead>
              <tr>
                <th>Tipo de asistencia</th>
                <th class="text-right">Total</th>
                <th class="text-right">En proceso</th>
                <th class="text-right">Validados</th>
                <th class="text-right">Rechazados</th>
                <th class="text-right">Monto pagado</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in report.refunds.byService" :key="row.service">
                <td>{{ row.service }}</td>
                <td class="text-right">{{ row.total }}</td>
                <td class="text-right">
                  <span style="color:#FF9800;">{{ row.inProcess }}</span>
                </td>
                <td class="text-right">
                  <span style="color:#4CAF50;">{{ row.approved }}</span>
                </td>
                <td class="text-right">
                  <span style="color:#F44336;">{{ row.rejected }}</span>
                </td>
                <td class="text-right">{{ formatAmount(row.paidAmount) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="breakdown-total">
                <td class="font-weight-bold">Total</td>
                <td class="text-right font-weight-bold">{{ breakdownTotals.total }}</td>
                <td class="text-right font-weight-bold" style="color:#FF9800;">{{ breakdownTotals.inProcess }}</td>
                <td class="text-right font-weight-bold" style="color:#4CAF50;">{{ breakdownTotals.approved }}</td>
                <td class="text-right font-weight-bold" style="color:#F44336;">{{ breakdownTotals.rejected }}</td>
                <td class="text-right font-weight-bold">{{ formatAmount(breakdownTotals.paidAmount) }}</td>
              </tr>
            </tfoot>
          </v-simple-table>
        </template>
      </v-card>

    </template>

    <!-- Drill-down: quién usó y cuándo -->
    <v-dialog v-model="usageDialog" max-width="1200" scrollable>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>{{ usageDialogTitle }}</span>
          <v-btn icon @click="usageDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <div v-if="loadingDetail" class="text-center py-6">
            <v-progress-circular indeterminate color="primary" />
          </div>
          <div v-else-if="!detailRows.length" class="text-center grey--text py-6">Sin registros</div>
          <v-simple-table v-else dense>
            <thead>
              <tr>
                <th v-if="usageDialogSection === 'refunds'">Número de Solicitud</th>
                <th>Usuario</th>
                <th>RUT</th>
                <th class="text-right">Fecha</th>
                <th v-if="usageDialogSection === 'refunds'">Servicio</th>
                <th v-if="usageDialogSection === 'refunds'">Estado</th>
                <th v-if="usageDialogSection === 'refunds'">Origen</th>
                <th v-if="usageDialogSection === 'refunds'">Mascota</th>
                <th v-if="usageDialogSection === 'refunds'">Plan</th>
                <th v-if="usageDialogSection === 'refunds'" class="text-right">Monto reembolsado</th>
                <th v-if="usageDialogSection === 'refunds'">Cubierto por</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in detailRows" :key="i">
                <td v-if="usageDialogSection === 'refunds'">{{ row.referenceIndex != null ? row.referenceIndex : '—' }}</td>
                <td>{{ row.name }} {{ row.surname }}</td>
                <td>{{ row.rut || '—' }}</td>
                <td class="text-right" style="white-space: nowrap;">{{ row.date ? row.date.slice(0, 10) : '—' }}</td>
                <td v-if="usageDialogSection === 'refunds'">{{ row.serviceName || '—' }}</td>
                <td v-if="usageDialogSection === 'refunds'">{{ row.status || '—' }}</td>
                <td v-if="usageDialogSection === 'refunds'">{{ row.origin || '—' }}</td>
                <td v-if="usageDialogSection === 'refunds'">{{ row.petName }}</td>
                <td v-if="usageDialogSection === 'refunds'">{{ row.planName }}</td>
                <td v-if="usageDialogSection === 'refunds'" class="text-right">{{ row.amount != null ? formatAmount(row.amount) : '—' }}</td>
                <td v-if="usageDialogSection === 'refunds'">
                  <v-chip x-small :color="isThirdPartySupplier(row) ? 'orange darken-1' : 'grey lighten-1'" :dark="isThirdPartySupplier(row)">
                    {{ row.supplierName || 'Pawer' }}
                  </v-chip>
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Drill-down: composición del Total de usos -->
    <v-dialog v-model="totalUsosDialog" max-width="480">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Composición del total de usos</span>
          <v-btn icon @click="totalUsosDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <v-simple-table dense>
            <tbody>
              <tr v-for="row in totalUsosBreakdown" :key="row.label">
                <td>{{ row.label }}</td>
                <td class="text-right font-weight-medium">{{ row.value }}</td>
              </tr>
              <tr class="breakdown-total">
                <td class="font-weight-bold">Total</td>
                <td class="text-right font-weight-bold">{{ totalUsos }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Drill-down: motivos de rechazo -->
    <v-dialog v-model="rejectionDialog" max-width="720" scrollable>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span>Motivos de rechazo</span>
          <v-btn icon @click="rejectionDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <div v-if="loadingDetail" class="text-center py-6">
            <v-progress-circular indeterminate color="primary" />
          </div>
          <div v-else-if="!rejectionRows.length" class="text-center grey--text py-6">Sin rechazos</div>
          <v-simple-table v-else dense>
            <thead>
              <tr><th>Usuario</th><th>Servicio</th><th>Motivo</th><th class="text-right">Fecha</th></tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in rejectionRows" :key="i">
                <td>{{ row.name }} {{ row.surname }}</td>
                <td>{{ row.service }}</td>
                <td>{{ row.reason || '—' }}</td>
                <td class="text-right" style="white-space: nowrap;">{{ row.date ? row.date.slice(0, 10) : '—' }}</td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" timeout="3000" top>
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import ContratoService from '@/services/contrato.service';
import CompanyReportService from '@/services/company-report.service';
import MRRService from '@/services/mrr.service';
import ContractProfitabilityService from '@/services/contract-profitability.service';
import CountryService from '@/services/countries.service';
import RefundDonutChart from '@/components/RefundDonutChart.vue';
import { utilsMixin } from '@/mixins/utilsMixin';
import { can } from '@/utils/permissions';
import { scoreColor } from '@/utils/customer-success.utils';

export default {
  name: 'CompanyReportSection',
  components: { RefundDonutChart },
  mixins: [utilsMixin],
  props: {
    companyId: {
      type: String,
      default: null,
    },
    // Permite llegar desde otra vista (ej. Métricas de Reembolsos) apuntando
    // directo a un contrato específico en vez del primero de la empresa.
    preselectContratoId: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      // Los listados nombran a cada usuario (nombre, RUT, correo): cada uno exige su tag.
      canSeeUsageUsers: can('companies:view-usage-users'),
      canSeeRefundUsers: can('companies:view-refund-users'),
      contratos: [],
      loadingContratos: false,
      selectedContrato: null,
      loading: false,
      loadError: false,
      report: null,
      basis: 'active',
      // Rango del reporte, en dos pares. `fromDate`/`toDate` es lo que el
      // usuario está eligiendo en los selectores; `appliedFrom`/`appliedTo` es
      // el rango con el que se consultó la data que se está viendo. Separarlos
      // permite elegir ambas fechas con calma y buscar una sola vez.
      fromDate: '',
      toDate: '',
      appliedFrom: '',
      appliedTo: '',
      fromDateMenu: false,
      toDateMenu: false,
      countries: [],
      mrr: null,
      mrrCurrency: '',
      mrrSharePct: null,
      marginPct: null,
      loadingExtraMetrics: false,
      usageDialog: false,
      totalUsosDialog: false,
      usageDialogTitle: '',
      usageDialogSection: '',
      detailRows: [],
      loadingDetail: false,
      rejectionDialog: false,
      rejectionRows: [],
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'green',
    };
  },

  async mounted() {
    // CountryService relanza la excepción en vez de devolver {status:'error'}
    // (a diferencia del resto de servicios) — sin este try/catch, una falla acá
    // (ej. countries caído) cortaba el mounted() entero y los contratos nunca
    // se cargaban.
    try {
      const countriesRes = await CountryService.getAllCountries(true);
      if (countriesRes.status === 'ok') {
        this.countries = countriesRes.data || [];
      }
    } catch (error) {
      this.countries = [];
    }
    this.loadingContratos = true;
    const res = await ContratoService.getAll();
    if (res.status === 'ok') {
      // Se incluyen todos los estados (no solo Activo): una empresa puede
      // tener solo contratos Churned/Pausado y antes no aparecía nada en el
      // selector. El estado se marca en el label para no confundirlos.
      let contratos = res.data || [];
      if (this.companyId) {
        contratos = contratos.filter(
          c => String(c.companyId || (c.company && c.company.id)) === String(this.companyId)
        );
      }
      this.contratos = contratos;
      // Cuando se abre acotado a una empresa, auto-seleccionar el contrato
      // pedido (si vino uno), si no el primero Activo, si no el que sea.
      if (this.companyId && this.contratos.length) {
        const preselected = this.preselectContratoId
          ? this.contratos.find(c => String(c.id) === String(this.preselectContratoId))
          : null;
        const defaultContrato =
          this.contratos.find(c => c.status === 'Activo') || this.contratos[0];
        this.selectedContrato = preselected || defaultContrato;
      }
    } else {
      this.showErrorSnackbar('No se pudieron cargar los contratos');
    }
    this.loadingContratos = false;
  },

  computed: {
    basisCount() {
      if (!this.report) return 1;
      return this.basis === 'active'
        ? this.report.memberships.active || 1
        : this.report.memberships.historical || 1;
    },

    usageSections() {
      if (!this.report) return [];
      const u = this.report.usage;
      return [
        {
          key: 'sponsorships',
          label: 'Apadrinamiento',
          icon: 'mdi-paw',
          color: '#9C27B0',
          total: u.sponsorships.total,
          uniqueUsers: u.sponsorships.uniqueUsers,
        },
        {
          key: 'televet',
          label: 'Televeterinaria',
          icon: 'mdi-video',
          color: '#2196F3',
          total: u.televet.total,
          uniqueUsers: u.televet.uniqueUsers,
        },
        {
          key: 'petPhotos',
          label: 'Fotos de mascota',
          icon: 'mdi-camera',
          color: '#FF9800',
          total: u.petPhotos.total,
          uniqueUsers: u.petPhotos.uniqueUsers,
        },
        {
          key: 'medicalRecords',
          label: 'Ficha médica',
          icon: 'mdi-medical-bag',
          color: '#4CAF50',
          total: u.medicalRecords.total,
          uniqueUsers: u.medicalRecords.uniqueUsers,
        },
        {
          key: 'discountClub',
          label: 'Club de Descuentos',
          icon: 'mdi-tag-multiple',
          color: '#E91E63',
          total: 0,
          uniqueUsers: 0,
          mock: true,
        },
      ];
    },

    // Reembolsos aprobados + pagados (ya incluye PetMed y Operativos, sumados
    // al balde "aprobados" en el backend porque no tienen flujo de estados
    // propio: son costos ya incurridos).
    totalReembolsos() {
      if (!this.report) return 0;
      const b = this.report.refunds.byStatus;
      return (b.approved || 0) + (b.paid || 0);
    },

    // Total de reembolsos, más los usos de cada sección de la app
    // (apadrinamiento, televet, fotos, ficha médica, club de descuentos).
    totalUsos() {
      if (!this.report) return 0;
      const seccionesUsos = this.usageSections.reduce((sum, s) => sum + (s.total || 0), 0);
      return this.totalReembolsos + seccionesUsos;
    },

    // "Aprobados" (byStatus.approved) viene desde el backend con PetMed y
    // Operativos ya sumados adentro (no tienen flujo de estados propio). Acá
    // se separan para mostrarlos como su propio segmento/fila sin duplicar
    // el conteo total.
    appApprovedCount() {
      if (!this.report) return 0;
      const b = this.report.refunds.byStatus;
      const petmedCount = this.report.refunds.petmed.count || 0;
      const operationalCount = this.report.refunds.operational.count || 0;
      return (b.approved || 0) - petmedCount - operationalCount;
    },

    // Métricas del donut, con "Aprobados" separado de PetMed/Operativos para
    // que cada uno tenga su propio segmento y color.
    donutMetrics() {
      if (!this.report) return {};
      const b = this.report.refunds.byStatus;
      return {
        ...b,
        approved: this.appApprovedCount,
        petmed: this.report.refunds.petmed.count || 0,
        operational: this.report.refunds.operational.count || 0,
      };
    },

    // Detalle de cada componente que suma el Total de usos (mismo criterio
    // que el cálculo de totalUsos, desglosado fila por fila).
    totalUsosBreakdown() {
      if (!this.report) return [];
      const b = this.report.refunds.byStatus;
      return [
        ...this.usageSections.map(s => ({
          label: s.label,
          value: s.mock ? 'sin tracking aún' : s.total,
        })),
        { label: 'Reembolsos aprobados (App)', value: this.appApprovedCount },
        { label: 'Reembolsos pagados', value: b.paid || 0 },
        { label: 'PetMed', value: this.report.refunds.petmed.count || 0 },
        { label: 'Operativos', value: this.report.refunds.operational.count || 0 },
      ];
    },

    todayDate() {
      return new Date().toISOString().substr(0, 10);
    },

    // Límites de la vigencia, usados para inicializar el rango y para comparar
    // contra él. Un contrato sin término llega hasta hoy.
    contratoBounds() {
      const contrato = this.selectedContrato;
      if (!contrato || !contrato.startDate) return null;
      return {
        from: String(contrato.startDate).substr(0, 10),
        to: contrato.endDate ? String(contrato.endDate).substr(0, 10) : this.todayDate,
      };
    },

    // Habilita "Vigencia completa" si cualquiera de los dos pares se movió: el
    // elegido o el ya aplicado. Así el botón sirve tanto para deshacer una
    // búsqueda como para descartar una selección que aún no se buscó.
    periodIsCustom() {
      const bounds = this.contratoBounds;
      if (!bounds) return false;
      return (
        this.fromDate !== bounds.from ||
        this.toDate !== bounds.to ||
        this.appliedFrom !== bounds.from ||
        this.appliedTo !== bounds.to
      );
    },

    hasPendingPeriodChange() {
      if (!this.fromDate || !this.toDate) return false;
      return this.fromDate !== this.appliedFrom || this.toDate !== this.appliedTo;
    },

    // Siempre se declara el período aplicado, no solo cuando excede la
    // vigencia: un sub-rango tampoco es "el contrato completo" y sus cifras son
    // más bajas, así que sin decirlo se leen como si fueran las del contrato.
    appliedPeriodLabel() {
      const period = this.report && this.report.period;
      if (!period) return '';
      return `${this.$options.filters.date(period.from)} — ${this.$options.filters.date(period.to)}`;
    },

    // Distingue el reporte del contrato completo de uno acotado, para que la
    // diferencia sea visible sin comparar fechas mentalmente.
    appliedPeriodIsFullContract() {
      const period = this.report && this.report.period;
      if (!period) return true;
      return period.from === period.contractFrom && period.to === period.contractTo;
    },

    // El backend clasifica el rango contra la vigencia; la vista solo traduce.
    // Se distingue el solapamiento parcial del total porque son situaciones
    // distintas para quien lee: en una parte de las cifras sí corresponde al
    // contrato, en la otra ninguna.
    periodWarning() {
      const coverage = this.report && this.report.period && this.report.period.coverage;
      if (coverage === 'outside') {
        return 'El período consultado está completamente fuera de la vigencia del contrato. '
          + 'Ninguna de las cifras de abajo corresponde al período contratado.';
      }
      if (coverage === 'partial') {
        return 'El período consultado excede la vigencia del contrato. '
          + 'Parte de las cifras de abajo ocurrió fuera del período contratado.';
      }
      return '';
    },

    // Vigencia del contrato elegido. Cuando no hay fecha de término se dice
    // así y se aclara hasta dónde llega el reporte, porque el uso se acota a
    // esta ventana y si no, la ausencia de datos recientes se lee como error.
    contratoPeriod() {
      const contrato = this.selectedContrato;
      if (!contrato || !contrato.startDate) return null;
      const desde = this.$options.filters.date(contrato.startDate);
      if (contrato.endDate) {
        return {
          label: `${desde} — ${this.$options.filters.date(contrato.endDate)}`,
          note: '',
        };
      }
      return {
        label: `${desde} — sin fecha de término`,
        note: 'el reporte llega hasta hoy',
      };
    },

    // El % lo calcula el backend (helper compartido con Métricas de Customer
    // Success). Calcularlo acá era la causa de que las dos vistas mostraran
    // números distintos para el mismo contrato.
    activationPct() {
      const activation = this.report && this.report.activation;
      return activation ? activation.pct : null;
    },

    // La base cambia según la línea de negocio, así que hay que decir sobre
    // qué se está midiendo: si no, un 135% sobre cuota se lee como error.
    activationFoot() {
      const activation = this.report && this.report.activation;
      if (!activation || activation.pct == null) return 'sin base para calcular';
      return activation.basis === 'quota'
        ? `${activation.denominator} contratadas`
        : `sobre ${activation.denominator} cuentas totales`;
    },

    activationTooltip() {
      const activation = this.report && this.report.activation;
      if (!activation || activation.pct == null) return '';
      const base = activation.basis === 'quota' ? 'contratadas (bolsa)' : 'cuentas totales';
      return `${activation.numerator} membresías activas sobre ${activation.denominator} ${base}`;
    },

    // % Uso: membresías con al menos un uso (reembolso, ficha médica o televet) en el período,
    // sobre las membresías activas; no sigue el toggle, igual que la activación. Lo calcula el
    // backend con la misma consulta que Métricas de Customer Success.
    usagePct() {
      const usageRate = this.report && this.report.usageRate;
      return usageRate ? usageRate.pct : null;
    },

    usageFoot() {
      const usageRate = this.report && this.report.usageRate;
      if (!usageRate || usageRate.pct == null) return 'sin membresías para calcular';
      return `${usageRate.numerator} de ${usageRate.denominator} membresías activas`;
    },

    // KPIs de «Base»: lo único que cambia con el toggle Activas/Totales. Usuarios y cuentas se
    // miden por el acceso al contrato (activo = vigente y con créditos; totales incluye los
    // revocados), no por las membresías. El backend entrega ambas versiones, así que cambiar
    // el toggle no vuelve a consultar.
    baseItems() {
      const m = this.report.memberships;
      const active = this.basis === 'active';
      const access = (active ? m.activeAccess : m.allAccess) || { users: 0, accounts: 0 };
      return [
        { key: 'users', label: active ? 'Usuarios activos' : 'Usuarios totales', value: access.users },
        { key: 'accounts', label: active ? 'Cuentas activas' : 'Cuentas totales', value: access.accounts },
        { key: 'memberships', label: active ? 'Membresías activas' : 'Membresías totales', value: active ? m.active : m.historical }
      ];
    },

    // Las dos tasas del grupo «Activación y uso», con la misma forma para pintarlas igual.
    activationUsageRates() {
      return [
        { key: 'activation', label: '% Activación', pct: this.activationPct, foot: this.activationFoot, tooltip: this.activationTooltip },
        { key: 'usage', label: '% Uso', pct: this.usagePct, foot: this.usageFoot, tooltip: this.usageTooltip }
      ];
    },

    usageTooltip() {
      const usageRate = this.report && this.report.usageRate;
      if (!usageRate || usageRate.pct == null) return '';
      return `${usageRate.numerator} membresías con uso (reembolso, ficha médica o televet) sobre ${usageRate.denominator} membresías activas`;
    },

    // rechazadas / total de refunds-requests (registradas + en proceso +
    // resueltas), sin PetMed/Operativos: esos canales no tienen flujo de
    // rechazo propio, así que incluirlos diluiría la tasa real del proceso
    // de revisión. Denominador distinto al de `refundTotal` (que sí incluye
    // PetMed/Operativos para el donut).
    rejectionRate() {
      const b = this.report && this.report.refunds.byStatus;
      if (!b) return 0;
      const total = (b.registered || 0) + (b.inReview || 0) + (b.toCorrect || 0)
                  + this.appApprovedCount + (b.rejected || 0) + (b.paid || 0)
                  + (b.invalidPayment || 0);
      if (!total) return 0;
      return +(((b.rejected || 0) / total) * 100).toFixed(1);
    },

    refundTotal() {
      if (!this.report) return 0;
      const b = this.report.refunds.byStatus;
      return (b.registered || 0) + (b.inReview || 0) + (b.toCorrect || 0)
           + (b.approved || 0) + (b.rejected || 0) + (b.paid || 0)
           + (b.invalidPayment || 0);
    },

    breakdownTotals() {
      const rows = (this.report && this.report.refunds.byService) || [];
      return rows.reduce(
        (acc, r) => ({
          total: acc.total + (r.total || 0),
          inProcess: acc.inProcess + (r.inProcess || 0),
          approved: acc.approved + (r.approved || 0),
          rejected: acc.rejected + (r.rejected || 0),
          paidAmount: acc.paidAmount + (r.paidAmount || 0),
        }),
        { total: 0, inProcess: 0, approved: 0, rejected: 0, paidAmount: 0 }
      );
    },
  },

  methods: {
    // Mismo semáforo que Métricas de Customer Success; sin porcentaje, color neutro.
    scoreClass(pct) {
      return pct == null ? '' : `${scoreColor(pct)}--text text--darken-1`;
    },
    scoreColor,
    // El nombre de la empresa no se incluye acá: esta vista ya está acotada a
    // una sola empresa (companyId), mostrarlo era redundante y recortaba el
    // código/producto al filtrar.
    contratoLabel(c) {
      const producto = c.producto && c.producto.nombre;
      const base = producto ? `${c.codigo} — ${producto}` : c.codigo;
      // Activo es el estado esperado, no hace falta marcarlo; los demás
      // (Churned, Pausado) sí, para no confundirlos con uno vigente.
      return c.status && c.status !== 'Activo' ? `${base} (${c.status})` : base;
    },

    // Mismos colores que la tabla de Contratos, para consistencia visual.
    statusColor(s) {
      if (s === 'Activo') return 'green darken-1';
      if (s === 'Pausado') return 'orange darken-1';
      return 'grey darken-1';
    },

    formatAmount(value) {
      return this.$options.filters.clp(Number(value) || 0);
    },

    isThirdPartySupplier(row) {
      return Boolean(row.supplierName && row.supplierName !== 'Pawer');
    },

    formatMrr(value) {
      const n = Math.round(Number(value) || 0).toLocaleString('es-CL');
      return this.mrrCurrency ? `${n} ${this.mrrCurrency}` : `$${n}`;
    },

    canOpenUsage(sectionKey) {
      return sectionKey === 'refunds' ? this.canSeeRefundUsers : this.canSeeUsageUsers;
    },

    async openUsageDetail(section) {
      if (!this.selectedContrato || !this.canOpenUsage(section.key)) return;
      this.usageDialogTitle = `${section.label} — usuarios`;
      this.usageDialogSection = section.key;
      this.detailRows = [];
      this.usageDialog = true;
      this.loadingDetail = true;
      const res = await CompanyReportService.getUsageDetail(
        this.selectedContrato.id,
        section.key,
        this.basis,
        this.appliedFrom,
        this.appliedTo
      );
      this.detailRows = res.status === 'ok' ? res.data : [];
      this.loadingDetail = false;
    },

    openTotalUsosDetail() {
      this.totalUsosDialog = true;
    },

    async openRejectionReasons() {
      if (!this.selectedContrato || !this.canSeeRefundUsers) return;
      this.rejectionRows = [];
      this.rejectionDialog = true;
      this.loadingDetail = true;
      const res = await CompanyReportService.getRejectionReasons(
        this.selectedContrato.id,
        this.basis,
        this.appliedFrom,
        this.appliedTo
      );
      this.rejectionRows = res.status === 'ok' ? res.data : [];
      this.loadingDetail = false;
    },

    // El endpoint de Gross Margin se consulta por countryId. Se resuelve desde
    // el countryId del contrato si viene, o mapeando el nombre del país contra
    // la lista de países (robusto ante respuestas cacheadas sin countryId).
    resolveCountryId(contrato) {
      const c = contrato.company || {};
      if (c.countryId) return c.countryId;
      if (c.country && c.country.id) return c.country.id;
      const name = c.country && c.country.name;
      if (name) {
        const match = this.countries.find(
          x => (x.name || '').toLowerCase() === String(name).toLowerCase()
        );
        if (match) return match.id;
      }
      return null;
    },

    // Al elegir contrato el rango vuelve a la vigencia de ese contrato: heredar
    // el rango anterior haría leer cifras de un período que nadie eligió para
    // este contrato.
    resetPeriodToContrato() {
      const bounds = this.contratoBounds;
      this.fromDate = bounds ? bounds.from : '';
      this.toDate = bounds ? bounds.to : '';
      this.appliedFrom = this.fromDate;
      this.appliedTo = this.toDate;
      if (this.selectedContrato) this.loadReport(this.selectedContrato.id);
    },

    // El rango elegido no se consulta hasta acá: los selectores son un borrador
    // y esta es la única puerta hacia la data, para poder elegir ambas fechas
    // sin que cada clic dispare una búsqueda.
    applyPeriod() {
      if (!this.fromDate || !this.toDate) return;
      if (this.fromDate > this.toDate) {
        this.showErrorSnackbar('La fecha de inicio no puede ser posterior a la de término');
        return;
      }
      this.appliedFrom = this.fromDate;
      this.appliedTo = this.toDate;
      if (this.selectedContrato) this.loadReport(this.selectedContrato.id);
    },

    async loadReport(contratoId) {
      this.loading = true;
      this.loadError = false;
      this.report = null;
      const res = await CompanyReportService.getSummary(
        contratoId,
        this.basis,
        this.appliedFrom,
        this.appliedTo
      );
      if (res.status === 'ok') {
        this.report = res.data;
      } else {
        this.loadError = true;
        this.showErrorSnackbar('No se pudo cargar el reporte del contrato');
      }
      this.loading = false;
      this.loadExtraMetrics();
    },

    // MRR y Gross Margin viven en otros endpoints (MRR Breakdown y Gross Margin),
    // ambos ya desglosados por contrato. Se cargan aparte del reporte de uso.
    async loadExtraMetrics() {
      this.mrr = null;
      this.mrrCurrency = '';
      this.mrrSharePct = null;
      this.marginPct = null;
      const contrato = this.selectedContrato;
      if (!contrato) return;

      // Estas dos cajas se llenan después que el resto del reporte (vienen de
      // otros endpoints); sin este flag mostraban "—" un instante y después
      // el número real, dando la falsa impresión de que no había datos.
      this.loadingExtraMetrics = true;

      const mrrRes = await MRRService.getDetalleMRR();
      if (mrrRes.status === 'ok') {
        const allRows = mrrRes.data || [];
        // "MRR hoy" = mes calendario actual (igual que MRR Resumen). Así el
        // total cuadra con esa vista y el % del contrato es consistente.
        const now = new Date();
        const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        const monthRows = allRows.filter(r => (r.fecha || '').slice(0, 7) === currentMonth);
        const contratoRows = monthRows.filter(r => r.contrato === contrato.codigo);
        if (contratoRows.length) {
          this.mrr = contratoRows.reduce((sum, r) => sum + (Number(r.mrrLocal) || 0), 0);
          this.mrrCurrency = contratoRows[0].moneda || '';
          const contratoUsd = contratoRows.reduce((sum, r) => sum + (Number(r.mrrUSD) || 0), 0);
          const totalUsd = monthRows.reduce((sum, r) => sum + (Number(r.mrrUSD) || 0), 0);
          this.mrrSharePct = totalUsd > 0 ? +((contratoUsd / totalUsd) * 100).toFixed(2) : null;
        }
      }

      // Gross Margin solo está disponible para B2B EB y B2B Asistencias.
      const countryId = this.resolveCountryId(contrato);
      if (countryId) {
        // Se le pasa el mismo rango que al resto del reporte: sin esto pedía
        // los últimos 12 meses por defecto y el margen hablaba de un período
        // distinto al de las cifras que tiene al lado.
        // Los tres estados van explícitos: el contrato seleccionado puede estar
        // churned o pausado y su margen histórico sigue siendo válido.
        const gmRes = await ContractProfitabilityService.getContractProfitability(
          countryId,
          ['B2B Employee Benefit', 'B2B Asistencias'],
          this.appliedFrom,
          this.appliedTo,
          ['Activo', 'Churned', 'Pausado']
        );
        if (gmRes.status === 'ok') {
          const rows = (gmRes.data && gmRes.data.rows) || [];
          const row = rows.find(r => r.contratoId === contrato.id);
          if (row && row.marginPct != null) {
            this.marginPct = +(row.marginPct * 100).toFixed(1);
          }
        }
      }
      this.loadingExtraMetrics = false;
    },

  },

  watch: {
    selectedContrato(val) {
      if (val) {
        // resetPeriodToContrato dispara la carga con el rango ya inicializado
        // en la vigencia del contrato recién elegido.
        this.resetPeriodToContrato();
      } else {
        this.report = null;
        this.fromDate = '';
        this.toDate = '';
      }
    },
  },
};
</script>

<style scoped>
.report-title {
  font-size: 20px;
  font-weight: 700;
  color: #1B1D21;
  font-family: Inter, sans-serif;
}
.report-desc {
  font-size: 13px;
  color: #555;
  font-family: Inter, sans-serif;
}
.period-note {
  font-size: 12px;
  color: #555;
  font-family: Inter, sans-serif;
}
.period-note-hint {
  color: #8593A3;
  margin-left: 4px;
}
.period-alert {
  font-size: 13px;
  font-family: Inter, sans-serif;
}
.period-pending-hint {
  font-size: 12px;
  color: #B26500;
  font-family: Inter, sans-serif;
}
.applied-period {
  font-size: 12px;
  color: #555;
  font-family: Inter, sans-serif;
}
.applied-period--custom {
  color: #B26500;
}
.kpi-group {
  border-radius: 8px;
  height: 100%;
}
.kpi-group--focus {
  border-color: #104B94 !important;
}
.kpi-group-title {
  font-size: 12px;
  font-weight: 700;
  color: #1B1D21;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: Inter, sans-serif;
  margin-bottom: 12px;
}
.kpi-pair,
.kpi-trio {
  display: grid;
  gap: 16px;
}
.kpi-pair { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.kpi-trio { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.kpi-item { min-width: 0; }
.kpi-item + .kpi-item {
  border-left: 1px solid #E6EAF0;
  padding-left: 16px;
}
.kpi-value--money {
  font-size: 22px;
  line-height: 1.6;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.kpi-value--small { font-size: 24px; }
.usage-item-head {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
}
/* Las secciones bajan de fila cuando no caben. Cada ítem lleva su divisor a la izquierda, corrido
   1px hacia afuera: el de la primera columna queda fuera del contenedor y se recorta, así ninguna
   fila empieza con un divisor colgando. */
.usage-sections {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 16px 0;
  overflow: hidden;
}
.usage-sections .kpi-item {
  border-left: 1px solid #E6EAF0;
  margin-left: -1px;
  padding: 0 16px;
}
.kpi-sub { min-height: 20px; }
.kpi-label {
  font-size: 11px;
  color: #8593A3;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: Inter, sans-serif;
}
.kpi-value {
  font-size: 32px;
  font-weight: 700;
  color: #1B1D21;
  font-family: Inter, sans-serif;
  line-height: 1.3;
  margin-top: 4px;
}
.kpi-foot {
  font-size: 11px;
  color: #8593A3;
  font-family: Inter, sans-serif;
  margin-top: 2px;
}
.toggle-label {
  font-size: 13px;
  color: #555;
  font-family: Inter, sans-serif;
  white-space: nowrap;
}
.basis-toggle {
  border-radius: 8px !important;
  overflow: hidden;
  background-color: #fff !important;
}
.basis-toggle .toggle-btn {
  font-size: 12px !important;
  font-family: Inter, sans-serif !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
  padding: 0 16px !important;
}
.basis-toggle .v-btn--active {
  background-color: #104B94 !important;
  color: #fff !important;
}
.section-title {
  font-size: 14px;
  font-weight: 700;
  color: #8593A3;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: Inter, sans-serif;
}
.section-card-title {
  font-size: 13px;
  font-weight: 600;
  color: #1B1D21;
  font-family: Inter, sans-serif;
}
.usage-count {
  font-size: 26px;
  font-weight: 700;
  color: #1B1D21;
  font-family: Inter, sans-serif;
  line-height: 1.2;
}
.usage-clickable {
  color: #104B94;
  cursor: pointer;
  text-decoration: underline;
}
.usage-sub {
  font-size: 11px;
  color: #8593A3;
  font-family: Inter, sans-serif;
  margin-top: 2px;
}
.basis-hint {
  font-size: 11px;
  color: #8593A3;
  font-family: Inter, sans-serif;
  font-style: italic;
}
.pct-label { font-size: 11px; color: #8593A3; font-family: Inter, sans-serif; }
.pct-value { font-size: 12px; font-weight: 600; font-family: Inter, sans-serif; }
.stat-card {
  border-radius: 8px;
  height: 120px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.stat-label {
  font-size: 11px;
  color: #8593A3;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-family: Inter, sans-serif;
}
.stat-value {
  font-size: 24px;
  font-weight: 700;
  font-family: Inter, sans-serif;
  line-height: 1.2;
  margin: 4px 0;
}
.stat-sub {
  font-size: 10px;
  color: #8593A3;
  font-family: Inter, sans-serif;
}
</style>
